import { useEffect, useRef, useState } from "react";
import { MapPin, X, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Free autocomplete on 35k+ French cities via /api/cities/search (proxy to geo.api.gouv.fr).
 * Props:
 *  - value: { name, dept, lat, lon } | null
 *  - onChange(city | null)
 */
export default function CityCombobox({ value, onChange, placeholder = "Rechercher une ville…", testid = "city-combobox" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const t = useRef(null);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    clearTimeout(t.current);
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    t.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/cities/search", { params: { q, limit: 12 } });
        setResults(data);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(t.current);
  }, [q]);

  const pick = (c) => {
    onChange(c);
    setQ("");
    setOpen(false);
  };

  const clear = () => { onChange(null); setQ(""); };

  return (
    <div className="relative" ref={ref} data-testid={testid}>
      {value ? (
        <div className="flex items-center gap-2 border border-border rounded-xl h-12 px-3 bg-white">
          <MapPin className="w-4 h-4 text-brand" />
          <div className="flex-1 min-w-0">
            <div className="text-sm text-ink truncate">{value.name}</div>
            <div className="text-[10px] text-slate2">Dép. {value.dept}</div>
          </div>
          <button type="button" onClick={clear} className="text-slate-400 tap-scale" data-testid={`${testid}-clear`}><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              className="w-full border border-border rounded-xl h-12 pl-10 pr-9 bg-white text-sm outline-none focus:border-brand"
              value={q}
              placeholder={placeholder}
              onChange={(e) => { setQ(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              data-testid={`${testid}-input`}
            />
            {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />}
          </div>
          {open && results.length > 0 && (
            <ul className="absolute z-40 top-full left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-white border border-border rounded-xl shadow-lg py-1" data-testid={`${testid}-list`}>
              {results.map((c) => (
                <li key={`${c.name}-${c.dept}`}>
                  <button
                    type="button"
                    onClick={() => pick(c)}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-bone text-left"
                    data-testid={`${testid}-opt-${c.insee}`}
                  >
                    <span className="text-sm text-ink">{c.name}</span>
                    <span className="text-[10px] text-slate2">{c.dept}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
